import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import removeObjKeys from '@glass/common/modules/utils/removeObjKeys';

import useContentSeed from '@glass/shared/modules/content/useContentSeed';
import { SIX_HOUR_SEED } from '@glass/shared/modules/random/seeds';
import { RANDOM_ORDER_BY } from '@glass/shared/modules/resources/orderBys';
import useTemplateMetas from '@glass/shared/modules/resumes/hooks/useTemplateMetas';
import contentTemplatesQuery from '@glass/shared/modules/resumes/queries/contentTemplates.client.graphql';

const useTemplate = ({
  seedOverride,
  slug,
  skip,
  ssr,
  jSlug,
  iSlug,
  fallbackId = SIX_HOUR_SEED,
  onCompleted,
}) => {
  const seed = useContentSeed({ seedOverride, jSlug, iSlug, fallbackId });

  if (!slug && !seed && !skip) {
    throw new Error(`invalid params @ useTemplate ${seed}, ${slug}, ${skip}`);
  }

  const { templateMetas } = useTemplateMetas();

  const options = useMemo(() => {
    return {
      variables:
        slug && templateMetas?.some(({ slug: metaSlug }) => slug === metaSlug)
          ? { where: { slug }, first: 1 }
          : { first: 1, orderBy: RANDOM_ORDER_BY, seed },
      skip: skip || (!slug && !seed),
      ssr,
      onCompleted,
    };
  }, [slug, templateMetas, seed, skip, ssr, onCompleted]);

  const { data, error, loading } = useQuery(contentTemplatesQuery, options);
  const { slug: templateSlug, template: templateData } = data?.contentTemplates?.[0] || {};
  const template = useMemo(
    () => templateData && removeObjKeys(templateData, ['id']),
    [templateData],
  );

  return {
    template,
    error,
    loading,
    templateSlug,
  };
};

export default useTemplate;
